import { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import DashboardHeader from "../components/DashboardHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, ImagePlus, X, Loader2, Package, Tag } from "lucide-react";

export default function Categories() {
  const { user } = useAuth();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteId, setDeleteId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/categories");
      setCategories(data || []);
    } finally { setLoading(false); }
  };

  useEffect(() => { if (user) load(); }, [user]);

  if (user === undefined) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">A carregar…</div>;
  if (user === null) return <Navigate to="/login" replace />;

  const topLevel = categories.filter((c) => !c.parent);
  const subs = categories.filter((c) => c.parent);
  const subsByParent = subs.reduce((acc, c) => {
    (acc[c.parent] = acc[c.parent] || []).push(c);
    return acc;
  }, {});

  const openNew = () => { setEditing(null); setFormOpen(true); };
  const openEdit = (c) => { setEditing(c); setFormOpen(true); };

  const doDelete = async () => {
    try {
      await api.delete(`/categories/${deleteId}`);
      toast.success("Categoria eliminada");
      setDeleteId(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao eliminar");
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="categories-page">
      <DashboardHeader title="Categorias" />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 lb-fade-in">
          <div>
            <div className="lb-overline">Organização</div>
            <h1 className="font-display text-3xl sm:text-4xl mt-1">Categorias</h1>
            <p className="text-sm text-muted-foreground mt-1">
              As categorias são criadas automaticamente quando adicionas produtos. Aqui podes editar nomes ou adicionar uma foto ilustrativa.
            </p>
          </div>
          <Button onClick={openNew} className="bg-[hsl(var(--primary))] hover:bg-[#A21F58] text-white" data-testid="new-category-btn">
            <Plus className="w-4 h-4 mr-1" /> Nova categoria
          </Button>
        </div>

        {loading ? (
          <div className="space-y-2">{Array.from({length:3}).map((_,i)=><div key={i} className="h-16 bg-white rounded-2xl lb-shadow-card animate-pulse" />)}</div>
        ) : topLevel.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-2xl lb-shadow-card">
            <Tag className="w-12 h-12 mx-auto text-muted-foreground/50" />
            <h3 className="mt-3 font-display text-xl">Sem categorias</h3>
            <p className="text-sm text-muted-foreground mt-1">Cria a primeira categoria ou adiciona um produto (a categoria é criada automaticamente).</p>
          </div>
        ) : (
          <div className="space-y-3">
            {topLevel.map((c) => (
              <section key={c.id} className="bg-white rounded-2xl lb-shadow-card p-4" data-testid={`category-${c.id}`}>
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-xl bg-secondary overflow-hidden flex items-center justify-center shrink-0">
                    {c.photo_base64 ? <img src={c.photo_base64} alt={c.name} className="w-full h-full object-cover" /> : <Package className="w-6 h-6 text-muted-foreground/60" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-display text-xl truncate">{c.name}</div>
                    <div className="text-xs text-muted-foreground">
                      {(subsByParent[c.name] || []).length} subcategoria(s)
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" onClick={() => openEdit(c)} data-testid={`edit-category-${c.id}`}><Pencil className="w-4 h-4" /></Button>
                    <Button variant="ghost" size="icon" onClick={() => setDeleteId(c.id)} data-testid={`delete-category-${c.id}`}><Trash2 className="w-4 h-4 text-[hsl(var(--destructive))]" /></Button>
                  </div>
                </div>
                {(subsByParent[c.name] || []).length > 0 && (
                  <div className="mt-3 pt-3 border-t border-border flex flex-wrap gap-2">
                    {subsByParent[c.name].map((sc) => (
                      <div key={sc.id} className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-secondary text-xs" data-testid={`subcategory-${sc.id}`}>
                        {sc.name}
                        <button type="button" onClick={() => openEdit(sc)} className="ml-1 text-muted-foreground hover:text-foreground"><Pencil className="w-3 h-3" /></button>
                        <button type="button" onClick={() => setDeleteId(sc.id)} className="text-muted-foreground hover:text-[hsl(var(--destructive))]"><X className="w-3 h-3" /></button>
                      </div>
                    ))}
                  </div>
                )}
              </section>
            ))}
          </div>
        )}
      </main>

      <CategoryFormDialog open={formOpen} onOpenChange={setFormOpen} category={editing} categories={categories} onSaved={load} />
      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar categoria?</AlertDialogTitle>
            <AlertDialogDescription>Não é possível eliminar categorias em uso por produtos.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-[hsl(var(--destructive))] hover:bg-[#B03838]">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function CategoryFormDialog({ open, onOpenChange, category, categories, onSaved }) {
  const isEdit = !!category?.id;
  const [form, setForm] = useState({ name: "", parent: "", photo_base64: "" });
  const [saving, setSaving] = useState(false);
  const topLevel = useMemo(() => categories.filter((c) => !c.parent), [categories]);

  useEffect(() => {
    if (open) {
      if (category) setForm({ name: category.name, parent: category.parent || "", photo_base64: category.photo_base64 || "" });
      else setForm({ name: "", parent: "", photo_base64: "" });
    }
  }, [open, category]);

  const handleImage = (file) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error("Máx 2MB"); return; }
    const reader = new FileReader();
    reader.onload = () => setForm((f) => ({ ...f, photo_base64: reader.result }));
    reader.readAsDataURL(file);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Nome obrigatório"); return; }
    setSaving(true);
    try {
      if (isEdit) await api.put(`/categories/${category.id}`, form);
      else await api.post("/categories", form);
      toast.success(isEdit ? "Atualizado" : "Criado");
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao guardar");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="category-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">{isEdit ? "Editar categoria" : "Nova categoria"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-2">
            <Label>Nome *</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ex: Perfumes" data-testid="category-name-input" />
          </div>
          <div className="space-y-2">
            <Label>Pertence a (opcional — deixa vazio para categoria principal)</Label>
            <select
              value={form.parent}
              onChange={(e) => setForm({ ...form, parent: e.target.value })}
              className="w-full border border-border rounded-md h-10 px-3 bg-white text-sm"
              data-testid="category-parent-select"
            >
              <option value="">— Categoria principal —</option>
              {topLevel.filter((c) => !isEdit || c.id !== category?.id).map((c) => (
                <option key={c.id} value={c.name}>{c.name}</option>
              ))}
            </select>
          </div>
          <div className="space-y-2">
            <Label>Foto ilustrativa (opcional)</Label>
            <div className="flex items-center gap-3">
              <label className="inline-flex items-center gap-2 px-3 py-2 border border-border rounded-md cursor-pointer bg-white hover:bg-secondary text-sm">
                <ImagePlus className="w-4 h-4" /> Escolher
                <input type="file" accept="image/*" className="hidden" onChange={(e) => handleImage(e.target.files?.[0])} data-testid="category-photo-input" />
              </label>
              {form.photo_base64 && (
                <>
                  <img src={form.photo_base64} alt="" className="w-12 h-12 object-cover rounded" />
                  <button type="button" onClick={() => setForm({ ...form, photo_base64: "" })} className="text-muted-foreground hover:text-[hsl(var(--destructive))]"><X className="w-4 h-4" /></button>
                </>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button type="submit" disabled={saving} className="bg-[hsl(var(--primary))] hover:bg-[#A21F58]" data-testid="save-category-btn">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Guardar
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
